import { Button } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { usePortal } from 'modules/core/lib/portalContext'

import type { RecommendationAction } from 'modules/timeoff/components/LeaveApprovalRecommendation/RecommendationAction'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import { trackApprovalRecommendation } from 'modules/timeoff/lib/tracking'
import type Leave from 'modules/timeoff/store/models/Leave'

interface RejectButtonProps {
  form: LeaveForm
  leave: Leave
  onReject: () => void
  onFetchStats: () => void
  onChange: () => void
  canApprove: boolean
  canUpdate: boolean
  canRemove: boolean
  isManagerOrAdmin: boolean | null | undefined
  isLoading: boolean
  isCurrentApprover?: boolean
  approvalFlowId?: number
  fromShifts?: boolean
  employeeId: number
  recommendationAction?: RecommendationAction
}

const RejectButton = ({
  form,
  leave,
  onReject,
  onFetchStats,
  onChange,
  canApprove,
  canUpdate,
  canRemove,
  isManagerOrAdmin,
  isLoading,
  isCurrentApprover,
  fromShifts = false,
  employeeId,
  recommendationAction,
}: RejectButtonProps) => {
  const { closePortal } = usePortal()
  const { fetchData } = useLeaves()

  const onClickReject = async () => {
    track(trackingEvents.timeoff.reviewTimeOffRequestSubmit, {
      request_action: 'reject_intermediate',
      from_shifts: fromShifts,
    })

    if (recommendationAction) {
      trackApprovalRecommendation('reject', recommendationAction)
    }

    if (canRemove && canUpdate) {
      await asyncAction(() => form.save(leave))
    }

    closePortal({
      preventOnCloseCallbackAfterClose: true,
    })

    return onReject()
  }

  const { performAction: onDestroy } = useAsyncAction(async () => {
    await leave.delete()
    onFetchStats()
    // Refreshes the context's leaves collection so that the deleted leave
    // is removed from the displayed list of leaves
    fetchData(employeeId)
    closePortal()

    if (onChange) onChange()
  })

  const onClick = canApprove || isCurrentApprover ? onClickReject : onDestroy
  const canReject = (canApprove || isCurrentApprover) && !leave.isApproved()

  if (!isManagerOrAdmin && leave.isRejected()) return null
  if (!canReject && !canRemove) return null

  const today = moment()
  const finish = moment(leave.attributes.finish_on).endOf('day')
  const pastLeave = today.isAfter(finish)

  const updateButton =
    isManagerOrAdmin && !leave.isRejected() && (leave.isPending() || !pastLeave)
      ? i18n.t('common.reject')
      : i18n.t('common.destroy')

  const button = canReject
    ? i18n.t('common.reject')
    : canRemove
      ? i18n.t('common.destroy')
      : updateButton

  return (
    <Button type="negative" onClick={onClick} disabled={isLoading}>
      {button}
    </Button>
  )
}

export default observer(RejectButton)
